/**
 * CheckInOverrideDialog - Staff check-in with face verification override.
 * Lets staff create a visit for a patient when face verification failed or wasn't possible.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { PersonAdd } from '@mui/icons-material';
import {
  createCheckInVisit,
  getPatientCMRWithHistory,
  getNormalizedPatientData,
} from '@healthdesk/shared-services';

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {function} props.onClose
 * @param {function} props.onSuccess - Called after visit created
 * @param {string} props.facilityId - Clinic/facility ID (used as clinicId for visits)
 * @param {Array<{ id: string, name: string }>} props.templates - Check-in templates (clinics) to choose from
 * @param {string} props.staffUid - Current staff user uid for faceVerifiedByStaff
 */
const CheckInOverrideDialog = ({
  open,
  onClose,
  onSuccess,
  facilityId,
  templates,
  staffUid,
}) => {
  const [templateId, setTemplateId] = useState('');
  const [patientUid, setPatientUid] = useState('');
  const [patientLookup, setPatientLookup] = useState(null); // { displayName, error? }
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleLookup = async () => {
    const uid = (patientUid || '').trim();
    if (!uid) {
      setPatientLookup({ error: 'Enter a patient UID' });
      return;
    }
    setPatientLookup(null);
    try {
      const normalized = await getNormalizedPatientData(uid);
      if (!normalized) {
        setPatientLookup({ error: 'Patient not found' });
        return;
      }
      const displayName = [normalized.firstName, normalized.lastName].filter(Boolean).join(' ') || 'Patient';
      setPatientLookup({ displayName });
    } catch (e) {
      setPatientLookup({ error: e.message || 'Lookup failed' });
    }
  };

  const handleSubmit = async () => {
    const uid = (patientUid || '').trim();
    if (!uid || !templateId || !facilityId || !staffUid) {
      setError('Please select a clinic and enter the patient UID.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const cmrData = await getPatientCMRWithHistory(uid);
      const normalized = await getNormalizedPatientData(uid);
      const hr = normalized || cmrData?.healthRecords || {};
      const patientData = {
        'patient.firstName': hr.firstName ?? '',
        'patient.lastName': hr.lastName ?? '',
        'patient.dateOfBirth': hr.dateOfBirth ?? null,
        'patient.sex': hr.sex ?? null,
        'patient.phone': hr.phone ?? null,
        'patient.email': hr.email ?? null,
        'patient.address': hr.address ?? null,
        'patient.emergencyContact': hr.emergencyContact ?? null,
      };

      const formData = {
        patientData,
        akaNames: cmrData?.akaNames || [],
        previousDOBs: cmrData?.previousDOBs || [],
        faceVerificationStatus: 'staff_override',
        faceVerifiedByStaff: { uid: staffUid, at: new Date() },
      };

      await createCheckInVisit(facilityId, uid, templateId, formData);
      setTemplateId('');
      setPatientUid('');
      setPatientLookup(null);
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.warn('[CheckInOverrideDialog]', err);
      setError(err.message || 'Failed to create check-in');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setError(null);
      setPatientLookup(null);
      onClose?.();
    }
  };

  const accessibleTemplates = templates || [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Check in with override</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use this when a patient could not complete face verification. The visit will be recorded with a staff override.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Clinic</InputLabel>
          <Select
            value={templateId}
            label="Clinic"
            onChange={(e) => setTemplateId(e.target.value)}
          >
            {accessibleTemplates.map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          label="Patient UID"
          placeholder="Firebase Auth UID of the patient"
          value={patientUid}
          onChange={(e) => {
            setPatientUid(e.target.value);
            setPatientLookup(null);
          }}
          helperText="Find this in the patient's profile or from a previous visit."
          sx={{ mb: 1 }}
        />
        {patientLookup?.displayName && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Patient: {patientLookup.displayName}
          </Typography>
        )}
        {patientLookup?.error && (
          <Alert severity="warning" sx={{ mb: 1 }}>{patientLookup.error}</Alert>
        )}
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Button size="small" variant="outlined" onClick={handleLookup}>
            Verify patient
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !templateId || !patientUid?.trim()}
          startIcon={submitting ? <CircularProgress size={16} /> : <PersonAdd />}
        >
          {submitting ? 'Creating visit...' : 'Check in (override)'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckInOverrideDialog;
